// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from '@/integrations/supabase/client';
import { Plus, Mail, Phone, Calendar, Users, Target, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { getSessionUser } from '@/lib/authSession';

interface Campaign {
  id: string;
  campaign_name: string;
  campaign_type: string;
  sport_focus: string;
  geographic_target: string;
  competitive_level_target: string;
  start_date: string;
  end_date: string;
  target_team_count: number;
  actual_contacts_made: number;
  responses_received: number;
  meetings_scheduled: number;
  registrations_achieved: number;
  campaign_status: string;
  budget_allocated: number;
  budget_spent: number;
}

interface OutreachActivity {
  id: string;
  team_id: string;
  activity_type: string;
  activity_date: string;
  subject: string;
  response_received: boolean;
  outcome: string;
  team_name?: string;
  coach_name?: string;
}

const TeamOutreachManager: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [activities, setActivities] = useState<OutreachActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [isNewCampaignOpen, setIsNewCampaignOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    campaign_name: '',
    campaign_type: '',
    sport_focus: '',
    geographic_target: '',
    competitive_level_target: '',
    start_date: '',
    end_date: '',
    target_team_count: '',
    budget_allocated: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get current user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', (await getSessionUser()).data.user?.id)
        .single();

      if (!profile) return;

      // Fetch campaigns
      const { data: campaignsData, error: campaignsError } = await supabase
        .from('team_recruitment_campaigns')
        .select('*')
        .eq('sales_rep_id', profile.id)
        .order('created_at', { ascending: false });

      if (campaignsError) throw campaignsError;

      // Fetch recent activities with team info
      const { data: activitiesData, error: activitiesError } = await supabase
        .from('team_outreach_activities')
        .select(`
          *,
          team_prospects (
            team_name,
            coach_name
          )
        `)
        .eq('sales_rep_id', profile.id)
        .order('activity_date', { ascending: false })
        .limit(20);

      if (activitiesError) throw activitiesError;

      setCampaigns(campaignsData || []);
      setActivities(activitiesData?.map(activity => ({
        ...activity,
        team_name: activity.team_prospects?.team_name,
        coach_name: activity.team_prospects?.coach_name
      })) || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load outreach data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', (await getSessionUser()).data.user?.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      const campaignData = {
        ...newCampaign,
        sales_rep_id: profile.id,
        target_team_count: parseInt(newCampaign.target_team_count),
        budget_allocated: parseFloat(newCampaign.budget_allocated),
        campaign_status: 'planning'
      };

      const { error } = await supabase
        .from('team_recruitment_campaigns')
        .insert([campaignData]);

      if (error) throw error;

      toast.success('Campaign created successfully');
      setIsNewCampaignOpen(false);
      setNewCampaign({
        campaign_name: '',
        campaign_type: '',
        sport_focus: '',
        geographic_target: '',
        competitive_level_target: '',
        start_date: '',
        end_date: '',
        target_team_count: '',
        budget_allocated: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error('Failed to create campaign');
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'completed': return 'secondary';
      case 'paused': return 'outline';
      default: return 'secondary';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'email': return <Mail className="h-4 w-4" />;
      case 'phone': return <Phone className="h-4 w-4" />;
      case 'meeting': return <Calendar className="h-4 w-4" />;
      default: return <Activity className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with New Campaign Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Outreach Campaigns</h2>
          <p className="text-muted-foreground">
            Manage recruitment campaigns and track outreach activities
          </p>
        </div>
        <Dialog open={isNewCampaignOpen} onOpenChange={setIsNewCampaignOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Campaign</DialogTitle>
              <DialogDescription>
                Set up a new team recruitment campaign
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="campaign_name">Campaign Name</Label>
                <Input
                  id="campaign_name"
                  value={newCampaign.campaign_name}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, campaign_name: e.target.value }))}
                  placeholder="Fall Football Recruitment"
                />
              </div>
              <div>
                <Label htmlFor="campaign_type">Campaign Type</Label>
                <Select onValueChange={(value) => setNewCampaign(prev => ({ ...prev, campaign_type: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seasonal">Seasonal</SelectItem>
                    <SelectItem value="event_specific">Event Specific</SelectItem>
                    <SelectItem value="geographic">Geographic</SelectItem>
                    <SelectItem value="sport_specific">Sport Specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="sport_focus">Sport Focus</Label>
                <Input
                  id="sport_focus"
                  value={newCampaign.sport_focus}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, sport_focus: e.target.value }))}
                  placeholder="Football, Basketball, etc."
                />
              </div>
              <div>
                <Label htmlFor="geographic_target">Geographic Target</Label>
                <Input
                  id="geographic_target"
                  value={newCampaign.geographic_target}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, geographic_target: e.target.value }))}
                  placeholder="Northern California"
                />
              </div>
              <div>
                <Label htmlFor="competitive_level_target">Competitive Level</Label>
                <Input
                  id="competitive_level_target"
                  value={newCampaign.competitive_level_target}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, competitive_level_target: e.target.value }))}
                  placeholder="High School Varsity"
                />
              </div>
              <div>
                <Label htmlFor="target_team_count">Target Team Count</Label>
                <Input
                  id="target_team_count"
                  type="number"
                  value={newCampaign.target_team_count}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, target_team_count: e.target.value }))}
                  placeholder="50"
                />
              </div>
              <div>
                <Label htmlFor="start_date">Start Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={newCampaign.start_date}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="end_date">End Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={newCampaign.end_date}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="budget_allocated">Budget Allocated</Label>
                <Input
                  id="budget_allocated"
                  type="number"
                  step="0.01"
                  value={newCampaign.budget_allocated}
                  onChange={(e) => setNewCampaign(prev => ({ ...prev, budget_allocated: e.target.value }))}
                  placeholder="5000.00"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsNewCampaignOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateCampaign}>
                Create Campaign
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campaigns Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {campaigns.slice(0, 3).map((campaign) => (
          <Card key={campaign.id}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{campaign.campaign_name}</CardTitle>
                <Badge variant={getStatusBadgeVariant(campaign.campaign_status)}>
                  {campaign.campaign_status}
                </Badge>
              </div>
              <CardDescription>{campaign.campaign_type}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Contacts Made:</span>
                  <span>{campaign.actual_contacts_made}/{campaign.target_team_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Responses:</span>
                  <span>{campaign.responses_received}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Registrations:</span>
                  <span>{campaign.registrations_achieved}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Budget Used:</span>
                  <span>${campaign.budget_spent}/${campaign.budget_allocated}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Activities */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Outreach Activities</CardTitle>
          <CardDescription>
            Latest communication with team prospects
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Team</TableHead>
                <TableHead>Coach</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Response</TableHead>
                <TableHead>Outcome</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.map((activity) => (
                <TableRow key={activity.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getActivityIcon(activity.activity_type)}
                      <span className="capitalize">{activity.activity_type}</span>
                    </div>
                  </TableCell>
                  <TableCell>{activity.team_name}</TableCell>
                  <TableCell>{activity.coach_name}</TableCell>
                  <TableCell>{activity.subject}</TableCell>
                  <TableCell>
                    {new Date(activity.activity_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={activity.response_received ? "default" : "secondary"}>
                      {activity.response_received ? "Yes" : "No"}
                    </Badge>
                  </TableCell>
                  <TableCell>{activity.outcome}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export { TeamOutreachManager };